/**
 * The donor's remembered identity (GH-600).
 *
 * ## Why this token is in localStorage when the staff console refuses to do that
 *
 * The staff console keeps its session in memory because that token administers an organization's
 * money, and a refresh signing you out is the correct price. This token is the opposite trade:
 * it can reach exactly one donor's own profile, check-ins, and engagement — never a donation,
 * receipt, payment, or another person — and the product requirement is "remember me for as long
 * as possible". For that blast radius, surviving the tab is the point and localStorage is the
 * honest tool. If this token ever gains more reach, this decision must be revisited first.
 */
import {
  donorAppleWebNonceResponseSchema,
  donorGiftListSchema,
  donorSignInOptionsSchema,
  type DonorAppleWebNonceResponse,
  type DonorGiftList,
  type DonorSignInOptions,
  donorEmailVerificationStartResponseSchema,
  donorPaymentMethodsResponseSchema,
  donorPledgesResponseSchema,
  type DonorPledge,
  donorSessionResponseSchema,
  donorCheckInResponseSchema,
  donorPhoneChangeConfirmResponseSchema,
  donorPhoneChangeStartResponseSchema,
  donorTicketsResponseSchema,
  donorProfileSchema,
  donorSignInStartResponseSchema,
  type DonorEmailVerificationStartResponse,
  type DonorPaymentMethodsResponse,
  engagementDetailResponseSchema,
  engagementListResponseSchema,
  engagementSubmitResponseSchema,
  errorResponseSchema,
  type DonorProfileWire,
  type DonorSessionResponse,
  type DonorCheckInResponse,
  type EngagementDetailResponse,
  type EngagementSubmitResponse,
  type EngagementPromptWire,
  type PostalAddressWire,
  type ReactionKindWire,
  reactionAckSchema,
  type ReactionAck,
  triviaAnswerAckSchema,
  type TriviaAnswerAck,
  type TriviaCorrectAnswerWire,
  triviaPlayerSchema,
  type TriviaPlayerWire,
  upvoteResponseSchema,
  type UpvoteResponse,
} from "@givehere/contracts";
import {
  organizationVisibilityResponseSchema,
  type OrganizationVisibilityResponse,
  type SetOrganizationVisibilityWire,
} from "@givehere/contracts";
import {
  donorMembershipResponseSchema,
  eventMembershipJoinResponseSchema,
  issuedInviteResponseSchema,
  memberInviteSchema,
  shareLinkResponseSchema,
  type DonorMembershipResponse,
  type EventMembershipJoinResponse,
  type IssuedInviteResponse,
  type MemberInviteWire,
  type MembershipPreferencesRequest,
  type RecordShareRequest,
  type ShareLinkQuery,
  type ShareLinkResponse,
} from "@givehere/contracts";

import { contentReportAckSchema, type ContentReportRequestInput } from "@givehere/contracts";
import { eventAccessHeaders } from "./event-access.js";
import {
  isPublicTriviaRoundJoinable,
  parseTriviaDonorRound,
  type TriviaDonorRound,
} from "./trivia-wire.js";

const API_BASE: string = import.meta.env["VITE_API_BASE_URL"] ?? "http://localhost:8080";
const STORAGE_KEY = "givehere.donor.session";
/** See `isReturningDonor` below: one flag, no personal data. */
const RETURNING_KEY = "givehere.donor.returning";

export class DonorApiError extends Error {
  readonly code: string;
  /** The HTTP status. A 404 on a membership route is `FEATURE_MEMBERSHIP` off (GH-864e). */
  readonly status: number;
  constructor(code: string, message: string, status = 0) {
    super(message);
    this.code = code;
    this.status = status;
  }
}

/* ------------------------------------------------------------------- storage */

export function storedToken(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    // Private browsing or storage disabled: the donor is simply not remembered on this device.
    return null;
  }
}

export function rememberToken(token: string): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, token);
    window.localStorage.setItem(RETURNING_KEY, "1");
  } catch {
    // Same: forgettable, not fatal.
  }
}

/**
 * "Somebody has signed in on this device before" (GH-856) — and nothing else.
 *
 * The give page uses it to make sign-in more prominent for a returning donor who is signed out
 * ("Welcome back — sign in"), so their saved card is one step away. It is a single flag on
 * purpose: no name, no phone, no card digits, no organization and no date, because it is read
 * *before* anyone has proved who is holding the phone, and a shared family tablet must not tell
 * the next person whose it was or where they gave. It survives sign-out — that is the moment it
 * exists for. Clearing site data clears it, like everything else this bundle keeps.
 */
export function isReturningDonor(): boolean {
  try {
    return window.localStorage.getItem(RETURNING_KEY) === "1";
  } catch {
    return false;
  }
}

export function forgetToken(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* nothing to forget */
  }
}

/* ------------------------------------------------------------------ requests */

async function request<T>(
  path: string,
  init: RequestInit,
  parse: (value: unknown) => T,
  token?: string | null,
): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      "content-type": "application/json",
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...init.headers,
    },
  });

  const payload: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const parsed = errorResponseSchema.safeParse(payload);
    throw new DonorApiError(
      parsed.success ? parsed.data.error.code : "internal_error",
      parsed.success ? parsed.data.error.message : "Something went wrong.",
      response.status,
    );
  }
  return parse(payload);
}

/* ------------------------------------------------------------------- sign-in */

/**
 * What a sign-in carries so a person with no account may make one while membership is on
 * (GH-871a `signUpAccessFields`): a member's invite code (or a waitlist admission code), or the
 * big screen's join token. An existing account signs in exactly as before and the server ignores
 * both; with membership off it ignores both too. Absent fields are not sent at all.
 */
export type SignUpAccess = { readonly inviteCode?: string; readonly eventJoinToken?: string };

export const startDonorSignIn = (
  phone: string,
): Promise<{ verificationId: string; developmentCode: string | null; last4: string }> =>
  request("/v1/donor/sign-in", { method: "POST", body: JSON.stringify({ phone }) }, (value) =>
    donorSignInStartResponseSchema.parse(value),
  );

export const confirmDonorSignIn = (
  body: {
    verificationId: string;
    code: string;
    phone: string;
  } & SignUpAccess,
): Promise<DonorSessionResponse> =>
  request("/v1/donor/sign-in/confirm", { method: "POST", body: JSON.stringify(body) }, (value) =>
    donorSessionResponseSchema.parse(value),
  );

/**
 * Confirm a phone code and remember the device — once (GH-856).
 *
 * The owner pressed Confirm, saw the dialog stay open, pressed it again, and was told "That code
 * has already been used" while in fact already signed in. The server is right to refuse a spent
 * code; a replayed confirm must never mint a second session. What was wrong is the client asking
 * twice and then reporting the refusal as the outcome.
 *
 * So when a confirm fails *after* this device already holds a session that the server still
 * honours — the first confirm landed and was remembered, and this is its echo — the established
 * session is the answer. `hadToken` is what the device held before the flow began: a token that
 * was already there is not evidence that *this* code worked, so it never rescues a failure.
 */
export async function completePhoneSignIn(
  body: { verificationId: string; code: string; phone: string } & SignUpAccess,
  hadToken: string | null,
): Promise<DonorSessionResponse> {
  try {
    const issued = await confirmDonorSignIn(body);
    rememberToken(issued.token);
    return issued;
  } catch (caught) {
    const current = storedToken();
    if (current && current !== hadToken) {
      const session = await fetchDonorSession(current).catch(() => null);
      if (session) return { ...session, token: current };
    }
    throw caught;
  }
}

/** Which ways in this deployment offers on the web (GH-856). Public. */
export const fetchSignInOptions = (): Promise<DonorSignInOptions> =>
  request("/v1/donor/sign-in/options", { method: "GET" }, (value) =>
    donorSignInOptionsSchema.parse(value),
  );

/**
 * A web provider door (GH-856 Apple, GH-859 Google): a single-use nonce from `<door>/nonce` — the
 * server keeps only its hash, and it is spendable at this door only — then the ID token at `<door>`.
 */
const startWebDoor = (door: string): Promise<DonorAppleWebNonceResponse> =>
  request(`${door}/nonce`, { method: "POST", body: "{}" }, (value) =>
    donorAppleWebNonceResponseSchema.parse(value),
  );

const confirmWebDoor = (
  door: string,
  body: { credential: string; nonce: string; displayName?: string } & SignUpAccess,
): Promise<DonorSessionResponse> =>
  request(door, { method: "POST", body: JSON.stringify(body) }, (value) =>
    donorSessionResponseSchema.parse(value),
  );

const APPLE_WEB = "/v1/donor/sign-in/apple/web";
const GOOGLE_WEB = "/v1/donor/sign-in/google/web";

export const startAppleWebSignIn = (): Promise<DonorAppleWebNonceResponse> =>
  startWebDoor(APPLE_WEB);

export const confirmAppleWebSignIn = (
  body: {
    credential: string;
    nonce: string;
    displayName?: string;
  } & SignUpAccess,
): Promise<DonorSessionResponse> => confirmWebDoor(APPLE_WEB, body);

export const startGoogleWebSignIn = (): Promise<DonorAppleWebNonceResponse> =>
  startWebDoor(GOOGLE_WEB);

export const confirmGoogleWebSignIn = (
  body: {
    credential: string;
    nonce: string;
  } & SignUpAccess,
): Promise<DonorSessionResponse> => confirmWebDoor(GOOGLE_WEB, body);

/**
 * The donor's own gifts and their receipt facts, newest first (GH-856 e). The same
 * `/v1/donor/gifts` the iPhone app's Receipts screen reads; nothing new on the server.
 */
export const fetchDonorGifts = (token: string, before?: string): Promise<DonorGiftList> =>
  request(
    `/v1/donor/gifts${before ? `?before=${encodeURIComponent(before)}` : ""}`,
    { method: "GET" },
    (value) => donorGiftListSchema.parse(value),
    token,
  );

/**
 * Which events this donor already holds admission for (owner-directed 2026-09-02).
 *
 * The event page offers "Get tickets" only to somebody who has none for *this* event, so what it
 * needs is a membership test, not a wallet. Deliberately reduced to a `Set` of event ids here
 * rather than handed to the screen whole: the orders carry tier names, prices, fair-market values
 * and issued admissions, none of which the event page has any business holding in order to decide
 * whether to draw one row.
 *
 * A failure is not an answer. The caller treats a rejected read as "unknown", which keeps the
 * tickets row visible — offering a second purchase to somebody who already bought is a mistake
 * they can back out of, and hiding the only way to buy from somebody who has not is one they
 * cannot.
 */
export const fetchDonorTicketedEventIds = (token: string): Promise<ReadonlySet<string>> =>
  request(
    "/v1/donor/tickets",
    { method: "GET" },
    (value) =>
      new Set(donorTicketsResponseSchema.parse(value).orders.map((order) => order.eventId)),
    token,
  );

/**
 * The donor's own promises and how much of each is still to pay (GH-809).
 *
 * `outstanding` is the server's subtraction, never this bundle's. Fulfilled promises are dropped
 * here: there is nothing left to pay on one, and a list of things to pay is what every caller
 * draws.
 */
export const fetchDonorPledges = (token: string): Promise<readonly DonorPledge[]> =>
  request(
    "/v1/donor/pledges",
    { method: "GET" },
    (value) =>
      donorPledgesResponseSchema
        .parse(value)
        .pledges.filter(
          (pledge) => pledge.state !== "fulfilled" && BigInt(pledge.outstanding.amountMinor) > 0n,
        ),
    token,
  );

export const fetchDonorSession = (token: string): Promise<DonorSessionResponse> =>
  request(
    "/v1/donor/session",
    { method: "GET" },
    (value) => donorSessionResponseSchema.parse(value),
    token,
  );

export const donorSignOut = (token: string): Promise<unknown> =>
  request("/v1/donor/sign-out", { method: "POST" }, (value) => value, token);

/* ------------------------------------------------------------------- profile */

export const updateDonorProfile = (
  token: string,
  body: {
    displayName?: string | null;
    email?: string | null;
    postalAddress?: PostalAddressWire | null;
  },
): Promise<DonorProfileWire> =>
  request(
    "/v1/donor/profile",
    { method: "PUT", body: JSON.stringify(body) },
    (value) => donorProfileSchema.parse(value),
    token,
  );

/* -------------------------------------------------- email verification */

/**
 * Ask for a code, then send it back (owner-directed 2026-08-25).
 *
 * Nothing about the donor's account waits on this. They can close the prompt, keep giving, and
 * confirm later or never — the address stays on the profile either way. What confirming buys the
 * donor is a receipt that reliably arrives; what it buys the platform is an address it can say it
 * proved. Neither is worth putting in front of a gift.
 */
export const startEmailVerification = (
  token: string,
  email: string,
): Promise<DonorEmailVerificationStartResponse> =>
  request(
    "/v1/donor/email/verification",
    { method: "POST", body: JSON.stringify({ email }) },
    (value) => donorEmailVerificationStartResponseSchema.parse(value),
    token,
  );

export const confirmEmailVerification = (
  token: string,
  body: { verificationId: string; code: string; email: string },
): Promise<DonorProfileWire> =>
  request(
    "/v1/donor/email/verification/confirm",
    { method: "POST", body: JSON.stringify(body) },
    (value) => donorProfileSchema.parse(value),
    token,
  );

/* --------------------------------------------------- a verified mobile (GH-883) */

/**
 * Add (or change) the mobile number on the signed-in profile, proved by a texted code — the same
 * `POST /v1/donor/phone` + `/confirm` pair the iPhone's "Add a mobile number" uses. Checking in and
 * the door prize ask for this where codes can be sent (`phone_verification_required`); a profile
 * that already holds a verified number is never asked. Both halves carry the session, so a code
 * proves a handset to *this* account and no other. The server throttles the sends and caps the
 * attempts; nothing here stores the code.
 */
export const startProfilePhoneVerification = (token: string, phone: string) =>
  request(
    "/v1/donor/phone",
    { method: "POST", body: JSON.stringify({ phone }) },
    (value) => donorPhoneChangeStartResponseSchema.parse(value),
    token,
  );

export const confirmProfilePhoneVerification = (
  token: string,
  body: { verificationId: string; code: string; phone: string },
) =>
  request(
    "/v1/donor/phone/confirm",
    { method: "POST", body: JSON.stringify(body) },
    (value) => donorPhoneChangeConfirmResponseSchema.parse(value),
    token,
  );

/* ---------------------------------------------------------- payment methods */

export const fetchPaymentMethods = (token: string): Promise<DonorPaymentMethodsResponse> =>
  request(
    "/v1/donor/payment-methods",
    { method: "GET" },
    (value) => donorPaymentMethodsResponseSchema.parse(value),
    token,
  );

/** Remove a saved instrument. The server tombstones it and detaches it provider-side. */
export const removeInstrument = (token: string, instrumentId: string): Promise<unknown> =>
  request(
    `/v1/donor/payment-instruments/${encodeURIComponent(instrumentId)}/remove`,
    { method: "POST" },
    (value) => value,
    token,
  );

export const setPaymentPreference = (
  token: string,
  method: "card" | "apple_pay" | "google_pay" | null,
): Promise<unknown> =>
  request(
    "/v1/donor/payment-methods/preference",
    { method: "PUT", body: JSON.stringify({ method }) },
    (value) => value,
    token,
  );

/* ---------------------------------------------- anonymous to all organizations (GH-858) */

export const fetchOrganizationVisibility = (
  token: string,
): Promise<OrganizationVisibilityResponse> =>
  request(
    "/v1/donor/organization-visibility",
    { method: "GET" },
    (value) => organizationVisibilityResponseSchema.parse(value),
    token,
  );

/**
 * The switch, both ways. The body's `confirm` literal is the server's guard against a stray call
 * (`setOrganizationVisibilitySchema`); the type makes a body without the right one unwritable here.
 */
export const setOrganizationVisibility = (
  token: string,
  body: SetOrganizationVisibilityWire,
): Promise<OrganizationVisibilityResponse> =>
  request(
    "/v1/donor/organization-visibility",
    { method: "PUT", body: JSON.stringify(body) },
    (value) => organizationVisibilityResponseSchema.parse(value),
    token,
  );

/* ------------------------------------------------------------------ check-in */

/** The session after a check-in; `doorPrizes` is optional so a plain session still fits. */
export type CheckedIn = DonorSessionResponse & {
  readonly doorPrizes?: DonorCheckInResponse["doorPrizes"];
};

/**
 * GH-871b: with membership on, a non-member checks in with the event's access token. GH-883: the
 * answer carries the door prizes the arrival entered (`doorPrizes`, empty when none), and a donor
 * with no verified mobile is refused with `phone_verification_required` where codes can be sent.
 */
export const donorCheckIn = (token: string, eventId: string): Promise<CheckedIn> =>
  request(
    "/v1/donor/check-ins",
    { method: "POST", body: JSON.stringify({ eventId }), headers: eventAccessHeaders(eventId) },
    (value) => donorCheckInResponseSchema.parse(value),
    token,
  );

export const donorCheckOut = (token: string, eventId: string): Promise<DonorSessionResponse> =>
  request(
    "/v1/donor/check-ins/checkout",
    { method: "POST", body: JSON.stringify({ eventId }) },
    (value) => donorSessionResponseSchema.parse(value),
    token,
  );

/* ---------------------------------------------------------------- engagement */

/**
 * The room's prompts. GH-871b: with membership on, the server shows them to a member (the session,
 * when this device holds one) or to the holder of the event's access token; with it off it reads
 * neither and answers as before.
 */
export const fetchEngagement = (eventId: string): Promise<readonly EngagementPromptWire[]> =>
  request(
    `/v1/public/events/${encodeURIComponent(eventId)}/engagement`,
    { method: "GET", headers: eventAccessHeaders(eventId) },
    (value) => engagementListResponseSchema.parse(value).prompts,
    storedToken(),
  );

/**
 * Report a comment, question or "Just now" entry (GH-958). No account: the event's access header
 * rides along as on the other public event reads. The answer is the same for a repeat.
 */
export const sendContentReport = (
  eventId: string,
  body: ContentReportRequestInput,
): Promise<{ received: true }> =>
  request(
    `/v1/public/events/${encodeURIComponent(eventId)}/content-reports`,
    { method: "POST", body: JSON.stringify(body), headers: eventAccessHeaders(eventId) },
    (value) => contentReportAckSchema.parse(value),
  );

export const fetchPromptDetail = (
  token: string,
  promptId: string,
): Promise<EngagementDetailResponse> =>
  request(
    `/v1/donor/prompts/${encodeURIComponent(promptId)}`,
    { method: "GET" },
    (value) => engagementDetailResponseSchema.parse(value),
    token,
  );

/**
 * Toggle a raised hand behind another attendee's question. The answer is the new count.
 *
 * Parsed rather than cast (GH-782). This function and its deleted twin in `api.ts` differed in
 * exactly this: one validated the shape and one asserted it, and a cast is a promise the
 * boundary has not kept — a server that stopped sending `votes` would reach the render as
 * `undefined` and paint an empty tally instead of failing here.
 */
export const upvoteQuestion = (token: string, submissionId: string): Promise<UpvoteResponse> =>
  request(
    `/v1/donor/submissions/${encodeURIComponent(submissionId)}/upvote`,
    { method: "POST" },
    (value) => upvoteResponseSchema.parse(value),
    token,
  );

/** `entryNumber` is the raffle ticket stub — stable across re-submits, null for other kinds. */
export const submitToPrompt = (
  token: string,
  promptId: string,
  body: { choice?: string; body?: string; anonymous?: boolean },
): Promise<EngagementSubmitResponse> =>
  request(
    `/v1/donor/prompts/${encodeURIComponent(promptId)}/submissions`,
    { method: "POST", body: JSON.stringify(body) },
    (value) => engagementSubmitResponseSchema.parse(value),
    token,
  );

/**
 * A reaction from a seat: floats up the event's big screen as an anonymous pulse.
 *
 * Nothing about the sender survives into the public read — the wall sees a kind and a sequence
 * number — so the acknowledgement is the whole answer. Rate limiting lives in the repository,
 * which means a client that taps quickly is answered rather than counted twice.
 */
export const sendReaction = (
  token: string,
  eventId: string,
  kind: ReactionKindWire,
): Promise<ReactionAck> =>
  request(
    `/v1/donor/events/${encodeURIComponent(eventId)}/reactions`,
    { method: "POST", body: JSON.stringify({ kind }) },
    (value) => reactionAckSchema.parse(value),
    token,
  );

/* ------------------------------------------------------------------- trivia (GH-912) */

/**
 * The room's live round, plus this donor's own standing in it — `GET
 * /v1/donor/events/:eventId/trivia/round`'s shape, read tolerantly (`trivia-wire.ts`). `round`
 * is null whenever no round is live; `myPlayerId`, `myScore`, and `hasAnswered` trail into null
 * together — a donor who has not joined this round's game gets the room's state with nothing
 * about themselves in it, never a fabricated zero score. Typed locally rather than from
 * contracts because the contracts trivia shapes are being extended in parallel and this card
 * must keep rendering through that shift.
 */
export type { TriviaDonorRound } from "./trivia-wire.js";

/**
 * The pre-check-in liveness probe (the fix for trivia being unreachable before check-in):
 * whether a joinable round is live, read from the PUBLIC big-screen poll
 * `GET /v1/public/events/:eventId/trivia/round` — the donor round read below requires a check-in
 * server-side, so before "here" this is the only honest way to learn there is anything to join.
 * Same access posture as `fetchEngagement`: the event-access headers (GH-871b) and whatever
 * session this device holds ride along. The answer is one boolean and nothing else — the public
 * payload's own content (question, tally, leaderboard, background) is the big screen's business,
 * never this gate's.
 */
export const fetchPublicTriviaRoundLive = (eventId: string): Promise<boolean> =>
  request(
    `/v1/public/events/${encodeURIComponent(eventId)}/trivia/round`,
    { method: "GET", headers: eventAccessHeaders(eventId) },
    isPublicTriviaRoundJoinable,
    storedToken(),
  );

/**
 * A donor joining the event's live trivia round. 404s with `not_found` whenever there is no live
 * round (`requireDonorVisibleEvent` and the live-round lookup both answer that way), which
 * `TriviaCard` reads the same way `fetchEngagement`'s callers read an unready prompt list: as
 * nothing to show, not as a page error. Answers with the player row, not the round state — a
 * caller that wants the round's shared state back reads it with `fetchTriviaRound` right after.
 */
export const joinTriviaRound = (
  token: string,
  eventId: string,
  displayName: string,
): Promise<TriviaPlayerWire> =>
  request(
    `/v1/donor/events/${encodeURIComponent(eventId)}/trivia/join`,
    { method: "POST", body: JSON.stringify({ displayName }) },
    (value) => triviaPlayerSchema.parse(value),
    token,
  );

/**
 * The round's live state, polled — donor-facing and carried on a token, unlike `fetchEngagement`,
 * because this answer is personalised (`myPlayerId`, `myScore`, `hasAnswered`) rather than a public
 * read with an optional one.
 */
export const fetchTriviaRound = (token: string, eventId: string): Promise<TriviaDonorRound> =>
  request(
    `/v1/donor/events/${encodeURIComponent(eventId)}/trivia/round`,
    { method: "GET" },
    parseTriviaDonorRound,
    token,
  );

/**
 * A player's submitted answer, scored server-side on the server's own receipt time — never the
 * client's elapsed time, the same discipline a donation's amount is held to (CLAUDE.md). The
 * response (GH-925) is an acknowledgement only: accepted plus the selection echoed back. Whether
 * it was right arrives through the round poll once time is up — an early submit must not be able
 * to learn the answer.
 */
export const submitTriviaAnswer = (
  token: string,
  roundId: string,
  questionId: string,
  submittedAnswer: TriviaCorrectAnswerWire,
): Promise<TriviaAnswerAck> =>
  request(
    `/v1/donor/trivia/rounds/${encodeURIComponent(roundId)}/answer`,
    { method: "POST", body: JSON.stringify({ questionId, submittedAnswer }) },
    (value) => triviaAnswerAckSchema.parse(value),
    token,
  );

/* ------------------------------------------------------- membership (GH-864e, DEC-051) */

/*
 * The donor's own membership: the waitlist, invites, the event auto-invite and two preferences.
 * Every route answers 404 while `FEATURE_MEMBERSHIP` is off; the reads turn that into `null` so the
 * screens hide, and the writes let it through as an ordinary `DonorApiError` with `status` 404.
 * Giving, receipts, saved cards and check-in never call any of these.
 */

/** True for the answer a membership route gives while its gate is off. */
const isMembershipGateOff = (caught: unknown): boolean =>
  caught instanceof DonorApiError && caught.status === 404;

async function unlessGateOff<T>(read: Promise<T>): Promise<T | null> {
  try {
    return await read;
  } catch (caught: unknown) {
    if (isMembershipGateOff(caught)) return null;
    throw caught;
  }
}

const parseMembership = (value: unknown): DonorMembershipResponse =>
  donorMembershipResponseSchema.parse(value);

export const fetchDonorMembership = (token: string): Promise<DonorMembershipResponse | null> =>
  unlessGateOff(request("/v1/donor/membership", { method: "GET" }, parseMembership, token));

export const joinWaitlist = (token: string): Promise<DonorMembershipResponse> =>
  request("/v1/donor/waitlist", { method: "POST", body: "{}" }, parseMembership, token);

/** Errors: `invite_unavailable` (uniform), `already_member`. */
export const redeemInvite = (token: string, code: string): Promise<DonorMembershipResponse> =>
  request(
    "/v1/donor/invites/redeem",
    { method: "POST", body: JSON.stringify({ code }) },
    parseMembership,
    token,
  );

/** The big screen's QR (GH-864, owner amendment 3). An expired token answers `waitlisted`. */
export const joinFromEvent = (
  token: string,
  joinToken: string,
): Promise<EventMembershipJoinResponse> =>
  request(
    "/v1/donor/membership/event-join",
    { method: "POST", body: JSON.stringify({ token: joinToken }) },
    (value) => eventMembershipJoinResponseSchema.parse(value),
    token,
  );

/** The code is in this answer exactly once; the server keeps only its HMAC. */
export const issueInvite = (token: string, idempotencyKey: string): Promise<IssuedInviteResponse> =>
  request(
    "/v1/donor/invites",
    { method: "POST", body: "{}", headers: { "idempotency-key": idempotencyKey } },
    (value) => issuedInviteResponseSchema.parse(value),
    token,
  );

/** A fresh code for an open invite; the old one stops working. */
export const reissueInvite = (token: string, inviteId: string): Promise<IssuedInviteResponse> =>
  request(
    `/v1/donor/invites/${encodeURIComponent(inviteId)}/reissue`,
    { method: "POST", body: "{}" },
    (value) => issuedInviteResponseSchema.parse(value),
    token,
  );

export const revokeInvite = (token: string, inviteId: string): Promise<MemberInviteWire> =>
  request(
    `/v1/donor/invites/${encodeURIComponent(inviteId)}/revoke`,
    { method: "POST", body: "{}" },
    (value) => memberInviteSchema.parse(value),
    token,
  );

export const updateMembershipPreferences = (
  token: string,
  body: MembershipPreferencesRequest,
): Promise<DonorMembershipResponse> =>
  request(
    "/v1/donor/membership/preferences",
    { method: "PUT", body: JSON.stringify(body) },
    parseMembership,
    token,
  );

/**
 * The link to share for an organization or an event. A member's carries `?ref=`; anyone else's is
 * plain. `null` when gated off — the caller then shares the page's own plain address.
 */
export const fetchShareLink = (
  token: string,
  target: ShareLinkQuery,
): Promise<ShareLinkResponse | null> => {
  const query = new URLSearchParams(
    "eventId" in target && target.eventId
      ? { eventId: target.eventId }
      : { organizationId: target.organizationId ?? "" },
  );
  return unlessGateOff(
    request(
      `/v1/donor/share-link?${query}`,
      { method: "GET" },
      (value) => shareLinkResponseSchema.parse(value),
      token,
    ),
  );
};

/** The share sheet finished. Cosmetic only (the streak); a refusal changes nothing on screen. */
export const recordShare = (token: string, kind: RecordShareRequest["kind"]): Promise<unknown> =>
  request(
    "/v1/donor/shares",
    { method: "POST", body: JSON.stringify({ kind }) },
    (value) => value,
    token,
  );
